'use client'

import { useState, useEffect } from 'react'
import { User, Property } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'



interface BulkOperationsProps {
  onUpdate: () => void
}

export default function BulkOperations({ onUpdate }: BulkOperationsProps) {
  const { token } = useUser()
  const toast = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  // const [propertiesData, setPropertiesData] = useState<any[]>([])
  // const [propertiesError, setPropertiesError] = useState<any>(null)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [selectedProperties, setSelectedProperties] = useState<string[]>([])
  const [operationType, setOperationType] = useState<'users' | 'properties'>('users')
  const [bulkAction, setBulkAction] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Load data
  const loadData = async () => {
    setLoading(true)
    try {
      if (!token) {
        setUsers([])
        setProperties([])
        return
      }

      const apiUrl = getApiUrl()

      const [usersRes, propsRes] = await Promise.all([
        fetch(`${apiUrl}/admin/users?limit=200`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${apiUrl}/admin/properties?limit=200`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ])

      const usersPayload = await usersRes.json().catch(() => ({}))
      const propsPayload = await propsRes.json().catch(() => ({}))

      if (!usersRes.ok) throw new Error(usersPayload?.message || 'Failed to load users')
      if (!propsRes.ok) throw new Error(propsPayload?.message || 'Failed to load properties')

      const mappedUsers: User[] = (usersPayload?.users || []).map((u: any) => ({
        id: u._id,
        email: u.email,
        role: u.role,
        firstName: u.firstName,
        lastName: u.lastName,
        phone: u.phone,
        is_verified: !!u.isVerified,
        created_at: u.createdAt || u.created_at
      }))

      const mappedProps: Property[] = (propsPayload?.properties || []).map((p: any) => ({
        id: p._id,
        title: p.title,
        description: p.description,
        price: p.price,
        location: p.location || `${p.address?.city || ''}${p.address?.state ? `, ${p.address.state}` : ''}`.trim(),
        images: Array.isArray(p.images) ? p.images.map((img: any) => img?.url || img).filter(Boolean) : [],
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms,
        area: p.squareFeet || 0,
        propertyType: p.propertyType,
        status: p.status,
        is_verified: !!p.isVerified,
        landlord_id: p.landlord?._id || p.landlord
      } as any))

      setUsers(mappedUsers)
      setProperties(mappedProps)
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load bulk operation data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Handle selection
  const handleSelectAll = (checked: boolean) => {
    if (operationType === 'users') {
      setSelectedUsers(checked ? users.map(u => u.id) : [])
    } else {
      setSelectedProperties(checked ? properties.map(p => p.id) : [])
    }
  }

  const handleSelectItem = (id: string, checked: boolean) => {
    if (operationType === 'users') {
      setSelectedUsers(prev => 
        checked ? [...prev, id] : prev.filter(item => item !== id)
      )
    } else {
      setSelectedProperties(prev => 
        checked ? [...prev, id] : prev.filter(item => item !== id)
      )
    }
  }

  // Execute bulk operation
  const executeBulkOperation = async () => {
    if (!bulkAction || (operationType === 'users' && selectedUsers.length === 0) || 
        (operationType === 'properties' && selectedProperties.length === 0)) {
      toast.error('Please select items and an action')
      return
    }

    setProcessing(true)
    try {
      if (!token) {
        toast.error('Please log in as an admin')
        return
      }

      const apiUrl = getApiUrl()
      const endpoint =
        operationType === 'users'
          ? `${apiUrl}/admin/bulk/users`
          : `${apiUrl}/admin/bulk/properties`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(
          operationType === 'users'
            ? { userIds: selectedUsers, action: bulkAction }
            : { propertyIds: selectedProperties, action: bulkAction }
        )
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Bulk operation failed')
      }

      toast.success(payload?.message || 'Bulk operation completed')

      setShowConfirmModal(false)
      setSelectedUsers([])
      setSelectedProperties([])
      setBulkAction('')
      await loadData()
      onUpdate()
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Bulk operation failed')
    } finally {
      setProcessing(false)
    }
  }

  // Get user status badge
  const getUserStatusBadge = (user: User) => {
    if ((user as any).suspended) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Suspended</span>
    } else if (user.is_verified) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Verified</span>
    } else {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>
    }
  }

  // Get property status badge
  const getPropertyStatusBadge = (property: Property) => {
    if (property.is_verified) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Verified</span>
    } else {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>
    }
  }

  const currentItems = operationType === 'users' ? users : properties
  const selectedItems = operationType === 'users' ? selectedUsers : selectedProperties
  const allSelected = currentItems.length > 0 && selectedItems.length === currentItems.length
  const someSelected = selectedItems.length > 0 && selectedItems.length < currentItems.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Bulk Operations</h2>
        <div className="flex space-x-2">
          <select
            value={operationType}
            onChange={(e) => {
              setOperationType(e.target.value as 'users' | 'properties')
              setSelectedUsers([])
              setSelectedProperties([])
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="users">Users</option>
            <option value="properties">Properties</option>
          </select>
        </div>
      </div>

      {/* Bulk Action Controls */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-blue-900">
                {selectedItems.length} {operationType} selected
              </h3>
              <p className="text-sm text-blue-700">
                Choose an action to perform on the selected {operationType}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Action</option>
                {operationType === 'users' ? (
                  <>
                    <option value="verify">Verify Users</option>
                    <option value="unverify">Unverify Users</option>
                    <option value="suspend">Suspend Users</option>
                    <option value="activate">Activate Users</option>
                    <option value="delete">Delete Users</option>
                  </>
                ) : (
                  <>
                    <option value="verify">Verify Properties</option>
                    <option value="unverify">Unverify Properties</option>
                    <option value="feature">Feature Properties</option>
                    <option value="unfeature">Unfeature Properties</option>
                    <option value="delete">Delete Properties</option>
                  </>
                )}
              </select>
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={!bulkAction}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                Execute Action
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {operationType.charAt(0).toUpperCase() + operationType.slice(1)} ({currentItems.length})
            </h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">Select All</span>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading {operationType}...</p>
          </div>
        ) : currentItems.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {currentItems.map((item) => (
              <div key={item.id} className="p-6">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    {operationType === 'users' ? (
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {(item as User).email}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Role: {(item as User).role} • Created: {new Date((item as User).created_at).toLocaleDateString()}
                        </p>
                        <div className="mt-1">
                          {getUserStatusBadge(item as User)}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {(item as Property).title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          ${(item as Property).price}/month • {(item as Property).location}
                        </p>
                        <div className="mt-1">
                          {getPropertyStatusBadge(item as Property)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No {operationType} found</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Confirm Bulk Operation</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to {bulkAction} {selectedItems.length} {operationType}?
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  This action cannot be undone. Please make sure you have selected the correct {operationType}.
                </p>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={executeBulkOperation}
                  disabled={processing}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400"
                >
                  {processing ? 'Processing...' : `Yes, ${bulkAction} ${selectedItems.length} ${operationType}`}
                </button>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={processing}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors disabled:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 


